import { Link } from "react-router-dom";
import {
  Store,
  ShoppingBag,
  Sparkles,
  IceCream2,
  CakeSlice,
  ArrowUpRight,
  Lock,
} from "lucide-react";
import { PRE_DESIGNED, DESSERTS_SHAKES } from "../lib/menuData";
import { BRAND } from "../lib/brand";
import { usePageMeta } from "../hooks/usePageMeta";
import { PAGE_SEO } from "../lib/seo";

const formatPrice = (n) => `$${n.toFixed(2)}`;

const MenuItem = ({ item, sectionId }) => {
  const hasPrice = typeof item.price === "number";
  return (
    <li
      data-testid={`menu-item-${sectionId}-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
      className="py-4 flex items-start justify-between gap-6 border-b border-white/5 last:border-0"
    >
      <div className="flex-1">
        <h3 className="text-base sm:text-lg text-white font-semibold tracking-wide">
          {item.name}
        </h3>
        {item.description && (
          <p className="text-sm text-gray-400 mt-1 leading-relaxed">{item.description}</p>
        )}
      </div>
      {hasPrice && (
        <span className="text-lg font-bold neon-blue whitespace-nowrap">
          {formatPrice(item.price)}
        </span>
      )}
    </li>
  );
};

const MenuSection = ({ section }) => (
  <section
    data-testid={`menu-section-${section.id}`}
    className="glass rounded-3xl border border-white/10 p-7 lg:p-10"
  >
    <div className="flex flex-wrap items-end justify-between gap-3 border-b border-white/10 pb-4 mb-3">
      <h3 className="font-script text-4xl text-white">{section.label}</h3>
      <div className="flex items-center gap-4">
        {section.flatPriceLabel && (
          <span className="text-[0.72rem] uppercase tracking-[0.25em] text-[#00b4ff]">
            {section.flatPriceLabel}
          </span>
        )}
        <span className="text-[0.7rem] uppercase tracking-[0.3em] text-gray-500">
          {section.items.length} items
        </span>
      </div>
    </div>
    {section.note && (
      <p className="text-xs uppercase tracking-[0.2em] text-[#ff1a8c] mb-2">{section.note}</p>
    )}
    <ul>
      {section.items.map((item) => (
        <MenuItem key={item.name} item={item} sectionId={section.id} />
      ))}
    </ul>
  </section>
);

const DesignYourOwnCard = ({ title, blurb, icon: Icon, href, testId }) => {
  const isAvailable = !!href;
  const commonClasses =
    "group relative overflow-hidden glass rounded-3xl border p-7 lg:p-9 transition-all";
  const stateClasses = isAvailable
    ? "border-white/10 hover:border-[#ff1a8c] hover:shadow-[0_0_30px_rgba(255,26,140,0.35)]"
    : "border-white/10 opacity-80";

  const inner = (
    <>
      <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full blur-3xl opacity-30 bg-gradient-to-br from-[#ff1a8c] to-[#00b4ff]" />
      <div className="relative">
        <div className="flex items-center justify-between mb-4">
          <span className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.3em] text-[#00b4ff]">
            <Sparkles className="w-3.5 h-3.5" /> Build your own
          </span>
          {isAvailable ? (
            <ArrowUpRight className="w-5 h-5 text-white opacity-60 group-hover:opacity-100 group-hover:text-[#ff1a8c] transition-all" />
          ) : (
            <Lock className="w-4 h-4 text-gray-500" />
          )}
        </div>
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-2xl bg-[#ff1a8c]/10 border border-[#ff1a8c]/40 flex items-center justify-center shrink-0">
            <Icon className="w-7 h-7 text-[#ff1a8c]" />
          </div>
          <div className="flex-1">
            <h3 className="font-script text-3xl sm:text-4xl text-white leading-tight mb-2">
              {title}
            </h3>
            <p className="text-sm text-gray-400 leading-relaxed">{blurb}</p>
            {!isAvailable && (
              <p className="mt-4 text-[0.7rem] uppercase tracking-[0.25em] text-gray-500">
                Menu file coming soon
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );

  if (isAvailable) {
    return (
      <a
        data-testid={testId}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={`${commonClasses} ${stateClasses}`}
      >
        {inner}
      </a>
    );
  }
  return (
    <div
      data-testid={testId}
      aria-disabled="true"
      className={`${commonClasses} ${stateClasses} cursor-not-allowed`}
    >
      {inner}
    </div>
  );
};

const Menus = () => {
  usePageMeta(PAGE_SEO.menus);
  const wafflesUrl = BRAND.designYourOwn?.wafflesPancakes || "";
  const iceCreamUrl = BRAND.designYourOwn?.rolledIceCream || "";

  return (
    <main data-testid="page-menus" className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
      {/* Header */}
      <div className="mb-14 text-center fade-up">
        <h1 className="font-script text-6xl sm:text-7xl neon-gradient-text">Dessert Menu</h1>
        <p className="text-gray-400 max-w-xl mx-auto mt-4">
          The full Hamilton dessert menu — rolled ice cream, waffles, pancakes and milkshakes
          made fresh to order at our Hillcrest dessert cafe. Pick a crowd favourite or build
          your own masterpiece.
        </p>
        <div className="flex items-center justify-center gap-6 mt-6 text-[0.7rem] uppercase tracking-[0.25em] flex-wrap">
          <span className="flex items-center gap-2 text-[#ff1a8c]"><Store className="w-3.5 h-3.5" /> Most items · in store only</span>
          <Link
            to="/shop"
            data-testid="menus-shop-online-pill"
            className="flex items-center gap-2 text-[#00b4ff] hover:text-white transition-colors"
          >
            <ShoppingBag className="w-3.5 h-3.5" /> Cookie Pies & Loaded Cookies · in store & online →
          </Link>
        </div>
      </div>

      {/* DESIGN YOUR OWN */}
      <section
        data-testid="design-your-own-section"
        className="grid md:grid-cols-2 gap-6 mb-16"
      >
        <DesignYourOwnCard
          title="Design Your Own Waffle / Pancakes"
          blurb="Waffles and pancakes Hamilton-made to order — choose your base, toppings & sauces and we'll plate up something nobody else has tried."
          icon={CakeSlice}
          href={wafflesUrl}
          testId="design-your-own-waffles"
        />
        <DesignYourOwnCard
          title="Design Your Own Rolled Ice Cream"
          blurb="Hamilton's freshest rolled ice cream — decide your flavour, mixings, toppings & sauces. Hand-rolled before your eyes on the frozen plate."
          icon={IceCream2}
          href={iceCreamUrl}
          testId="design-your-own-ice-cream"
        />
      </section>

      {/* PRE DESIGNED MENU */}
      <section className="mb-20">
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-[0.3em] text-[#ff1a8c] mb-3">Crowd favourites</p>
          <h2 className="font-script text-5xl sm:text-6xl text-white">{PRE_DESIGNED.title}</h2>
        </div>
        <div className="space-y-8">
          {PRE_DESIGNED.sections.map((s) => (
            <MenuSection key={s.id} section={s} />
          ))}
        </div>
        <p
          data-testid="pre-designed-footnote"
          className="mt-8 text-center text-[0.72rem] uppercase tracking-[0.25em] text-[#ff1a8c]"
        >
          {PRE_DESIGNED.footnote}
        </p>
      </section>

      {/* OTHER DESSERTS & SHAKES */}
      <section className="mb-20">
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-[0.3em] text-[#ff1a8c] mb-3">More to love</p>
          <h2 className="font-script text-5xl sm:text-6xl text-white">{DESSERTS_SHAKES.title}</h2>
        </div>
        <div className="space-y-8">
          {DESSERTS_SHAKES.sections.map((s) => (
            <MenuSection key={s.id} section={s} />
          ))}
        </div>
        <p
          data-testid="desserts-shakes-footnote"
          className="mt-8 text-center text-[0.72rem] uppercase tracking-[0.25em] text-[#00b4ff]"
        >
          {DESSERTS_SHAKES.footnote}
        </p>
      </section>

      {/* CTA */}
      <section className="text-center glass rounded-3xl border border-white/10 p-10 lg:p-14">
        <h2 className="font-script text-4xl sm:text-5xl text-white mb-4">
          Want it shipped instead?
        </h2>
        <p className="text-gray-400 max-w-md mx-auto mb-6">
          Our Cookie Pies & Loaded Cookies are available both in store and online — head to
          the shop to pick yours.
        </p>
        <Link to="/shop" data-testid="menus-cta-shop-link" className="neon-btn">
          Shop Online <ArrowUpRight className="w-4 h-4" />
        </Link>
      </section>
    </main>
  );
};

export default Menus;
